'use client';

import { PropsWithChildren } from 'react';
import { ArtistLogo } from '../components/atoms/artist-logo';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { getFontVariable } from '../components/utils';
import Link from 'next/link';
import { getPrefixPath } from '@/routes';
import { PageCollectionConfigFontFamily } from '@creator-suite/page-types';

export interface PageHeaderProps {
  host: string;
  domain: string;
  primaryColor: string;
  headerText: string | null | undefined;
  logoImageUrl: string | null | undefined;
  logoWidthPx: number;
  title: string;
  fontFamily: PageCollectionConfigFontFamily;

  mode?: 'logo-only' | 'default';
}

const variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.5,
    },
  },
};

const descriptionVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.5,
    },
  },
};

interface OptionalLinkProps {
  href: string | undefined;
}

const OptionalLink: React.FC<PropsWithChildren<OptionalLinkProps>> = ({
  href,
  children,
}) => {
  return href ? (
    <Link href={href} className="cursor-pointer">
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
};

export const PageHeader: React.FC<PropsWithChildren<PageHeaderProps>> = (
  props,
) => {
  const {
    title,
    logoImageUrl,
    fontFamily,
    logoWidthPx,
    headerText,
    primaryColor,
    children,
    host,
    domain,
    mode = 'default',
  } = props;

  const Logo = logoImageUrl ? (
    <OptionalLink href={getPrefixPath(host, domain) + '/'}>
      <ArtistLogo
        imageUrl={logoImageUrl}
        logoWidthPx={logoWidthPx}
        className="mx-auto h-30 w-auto sm:h-36 md:h-auto md:w-full"
      />
    </OptionalLink>
  ) : null;

  const HeaderText = headerText ? (
    <OptionalLink href={getPrefixPath(host, domain) + '/'}>
      <h1
        className="text-center text-5xl leading-5 font-bold md:text-7xl"
        style={{ color: primaryColor }}
      >
        {headerText}
      </h1>
    </OptionalLink>
  ) : null;

  return (
    <header className={clsx('mx-4 space-y-8')}>
      {mode === 'default' ? (
        <>
          {Logo}
          {HeaderText}
          <div className="relative w-full"></div>
          <motion.div
            initial="hidden" // Start with the hidden state
            animate="visible" // Animate to the visible state
            variants={variants} // Use the defined variants
          >
            <h1
              className={clsx(
                'text-center leading-8',
                'text-2xl md:text-4xl',
                getFontVariable(fontFamily),
              )}
            >
              {title}
            </h1>
          </motion.div>
          {children ? (
            <motion.div
              initial="hidden" // Start with the hidden state
              animate="visible" // Animate to the visible state
              variants={descriptionVariants} // Use the defined variants
            >
              {typeof children === 'string' ? (
                <p
                  className={clsx(
                    'text-center font-[inherit] text-xs whitespace-pre-wrap opacity-70',
                    getFontVariable(fontFamily),
                  )}
                >
                  {children}
                </p>
              ) : (
                <div
                  className={clsx(
                    'w-full space-y-4 text-center text-xs',
                    getFontVariable(fontFamily),
                  )}
                >
                  {children}
                </div>
              )}
            </motion.div>
          ) : null}
        </>
      ) : (
        <>{Logo || HeaderText}</>
      )}
    </header>
  );
};
